import { useEffect, useState, type MouseEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { navLinks, siteInfo } from "@/mocks/site";

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const scrollToId = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleAnchor = (e: MouseEvent<HTMLAnchorElement>, to: string) => {
    if (!to.startsWith("/#")) return;
    e.preventDefault();
    const id = to.slice(2);
    setOpen(false);
    if (location.pathname === "/") {
      scrollToId(id);
    } else {
      navigate("/");
      window.setTimeout(() => scrollToId(id), 160);
    }
  };

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    if (to.startsWith("/#")) return false;
    return location.pathname.startsWith(to);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-b border-background-200/80"
          : "bg-background-50/80 backdrop-blur"
      }`}
    >
      <div className="w-full px-4 md:px-6 h-16 md:h-18 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
          <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-fire-fill text-lg"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading font-bold text-lg text-foreground-950 tracking-wide">
              {siteInfo.brand}
            </span>
            <span className="text-[10px] font-label text-foreground-500 tracking-wider mt-0.5 hidden sm:block">
              {siteInfo.tagline}
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={(e) => handleAnchor(e, link.to)}
              className={`px-3.5 py-2 rounded-md text-sm font-label transition-colors cursor-pointer whitespace-nowrap ${
                isActive(link.to)
                  ? "text-primary-600 font-medium bg-primary-50"
                  : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/#subscribe"
            onClick={(e) => handleAnchor(e, "/#subscribe")}
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-notification-3-line w-4 h-4 flex items-center justify-center"></i>
            订阅热点
          </Link>
          <button
            type="button"
            aria-label={open ? "关闭菜单" : "打开菜单"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-md border border-background-300/70 text-foreground-800 cursor-pointer"
          >
            <i className={open ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-background-50 border-b border-background-200/80">
          <nav className="px-4 py-3 flex flex-col">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={(e) => handleAnchor(e, link.to)}
                className="py-3 px-2 rounded-md text-sm font-label text-foreground-800 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer border-b border-background-100 last:border-b-0"
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/#subscribe"
              onClick={(e) => handleAnchor(e, "/#subscribe")}
              className="mt-3 mb-2 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-notification-3-line w-4 h-4 flex items-center justify-center"></i>
              订阅热点
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}